import { lazy, Suspense, type ReactNode } from 'react';
import { Navigate, NavLink, Outlet, Route, Routes } from 'react-router-dom';
import { LogOut, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/layout/theme-toggle';
import { PageLoader } from '@/components/common/page-loader';
import { useAdminMe } from '@/hooks/use-admin';
import { panelErrorMessage } from './panel-error';

const PanelLogin = lazy(() => import('./panel-login').then((module) => ({ default: module.PanelLogin })));
const PanelUsers = lazy(() => import('./users').then((module) => ({ default: module.PanelUsers })));
const PanelUserDetail = lazy(() => import('./user-detail').then((module) => ({ default: module.PanelUserDetail })));
const PanelOperations = lazy(() => import('./operations').then((module) => ({ default: module.PanelOperations })));
const PanelPricing = lazy(() => import('./pricing').then((module) => ({ default: module.PanelPricing })));

function PanelAuthGuard({ children }: { children: ReactNode }) {
  const { isAuthed, isBooting, isUserLoading, logout } = useAuth();
  const admin = useAdminMe(isAuthed);

  if (isBooting || (isAuthed && (isUserLoading || admin.isLoading))) return <PageLoader />;
  if (!isAuthed) return <Navigate to="/login" replace />;
  if (admin.isError) {
    return <main className="grid min-h-screen place-items-center p-6"><div className="max-w-md space-y-3 text-center"><p role="alert">{panelErrorMessage(admin.error, 'Не удалось проверить права доступа.')}</p><Button type="button" variant="outline" onClick={() => void admin.refetch()}>Повторить</Button></div></main>;
  }
  if (admin.data?.is_admin !== true) {
    return <main className="grid min-h-screen place-items-center p-6"><div className="space-y-3 text-center"><p>У этой учётной записи нет доступа к панели.</p><Button type="button" variant="outline" onClick={logout}>Выйти</Button></div></main>;
  }
  return <>{children}</>;
}

function PanelLayout() {
  const { logout } = useAuth();
  return (
    <div className="min-h-screen bg-[var(--bg)]">
      <header className="border-b border-[var(--border)] bg-[var(--surface)]">
        <div className="mx-auto flex min-h-16 max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2 sm:flex-nowrap sm:px-5">
          <div className="flex items-center gap-2 sm:gap-4">
            <ShieldCheck className="h-5 w-5" />
            <span className="whitespace-nowrap font-semibold">aniphys panel</span>
          </div>
          <div className="flex items-center gap-2 sm:order-last">
            <ThemeToggle />
            <Button type="button" variant="ghost" size="sm" className="min-h-11 whitespace-nowrap" onClick={logout}>
              <LogOut className="h-4 w-4" />
              Выйти
            </Button>
          </div>
          <nav aria-label="Разделы панели" className="order-last flex w-full items-center gap-1 text-sm sm:order-none sm:w-auto">
            <NavLink to="/" end className={({ isActive }) => isActive ? 'flex min-h-11 items-center whitespace-nowrap rounded-md bg-[var(--surface-2)] px-3 py-2' : 'flex min-h-11 items-center whitespace-nowrap rounded-md px-3 py-2 text-[var(--text-muted)]'}>Управление</NavLink>
            <NavLink to="/prices" className={({ isActive }) => isActive ? 'flex min-h-11 items-center whitespace-nowrap rounded-md bg-[var(--surface-2)] px-3 py-2' : 'flex min-h-11 items-center whitespace-nowrap rounded-md px-3 py-2 text-[var(--text-muted)]'}>Цены</NavLink>
            <NavLink to="/operations" className={({ isActive }) => isActive ? 'flex min-h-11 items-center whitespace-nowrap rounded-md bg-[var(--surface-2)] px-3 py-2' : 'flex min-h-11 items-center whitespace-nowrap rounded-md px-3 py-2 text-[var(--text-muted)]'}>Операции</NavLink>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-7xl p-4 sm:p-8">
        <Outlet />
      </main>
    </div>
  );
}

export function PanelRoot() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route path="/login" element={<PanelLogin />} />
        <Route element={<PanelAuthGuard><PanelLayout /></PanelAuthGuard>}>
          <Route path="/" element={<PanelUsers />} />
          <Route path="/users/:userId" element={<PanelUserDetail />} />
          <Route path="/prices" element={<PanelPricing />} />
          <Route path="/operations" element={<PanelOperations />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
